/**
 * MovePreview (BOARD-RECO-PREVIEW-01) — the "review before you commit" step for
 * a cross-tech move. Seth's feedback on the one-click Move: "it just does it, no
 * undo… maybe a compare of the schedule, then adjustments, then save." So a move
 * now opens this preview FIRST:
 *   - the call's context (time window, type, the actual dispatch notes),
 *   - a side-by-side of BOTH techs' days — the source losing the call (struck),
 *     the target gaining it; the target list is DRAGGABLE so the dispatcher can
 *     slide the new stop to exactly where they want it in the schedule,
 *   - each route's stop-count + drive-miles before → after, recomputed live as
 *     you drag, and the net,
 *   - Confirm (writes the assignment, + saves the order if you dragged) or
 *     Cancel (writes nothing).
 * Routes are shown in window-aware optimized order so the mileage is realistic
 * (a strict clock sort invents detours and makes the numbers lie). Honors the
 * modal-close contract (outside-click + Esc + visible X).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowRight, MapPin, GripVertical } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/lib/cn";
import {
  formatTime,
  resolveWindowAndPriority,
  extractJobPreview,
} from "@/lib/format";
import { windowColorClasses } from "./api";
import { routeMiles, optimizeBanded } from "@/lib/route";
import { useRouteDrive } from "@/lib/driveTimes";
import type { Dispatch } from "@/lib/types";
import type { RecoTech, RecoStop } from "@/lib/recommendations";
import { InfoTip } from "@/components/InfoTip";

export interface PendingMove {
  dispatch: string;
  label: string;
  fromEmpno: string;
  fromName: string;
  toEmpno: string;
  toName: string;
  savingMiles: number;
}

export interface PendingReorder {
  techEmpno: string;
  techName: string;
  /** the tech's stops (coords) + the two orderings to compare */
  stops: RecoStop[];
  currentOrder: string[];
  proposedOrder: string[];
}

/**
 * One small stop row used by both previews. Static unless a drag handle is
 * wired in (the target/proposed lists pass onDragStart + dragging).
 */
function StopRow({
  stop,
  rows,
  tone,
  badge,
  onDragStart,
  dragging,
}: {
  stop: RecoStop;
  rows: Map<string, Dispatch>;
  tone: "stay" | "leaving" | "new";
  badge?: string;
  onDragStart?: (e: React.PointerEvent) => void;
  dragging?: boolean;
}) {
  const r = rows.get(stop.dispatch);
  const rw = resolveWindowAndPriority(r?.window_code, r?.notes);
  // Pegasus stops have no ESC row — fall back to the stop's own time/window.
  const tw = rw.timeWindow ?? stop.win ?? null;
  const rc = tw ? windowColorClasses(tw) : null;
  return (
    <div
      data-stopid={stop.dispatch}
      className={cn(
        "flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-shadow",
        tone === "leaving" &&
          "border-rose-200 bg-rose-50 text-slate-400 line-through",
        tone === "new" && "border-emerald-300 bg-emerald-50",
        tone === "stay" && "border-hairline bg-white",
        dragging && "pointer-events-none scale-[1.02] opacity-95 shadow-e2",
      )}
    >
      {onDragStart && (
        <span
          onPointerDown={onDragStart}
          aria-label="Drag to reorder"
          className="shrink-0 cursor-grab touch-none text-slate-300 transition-colors hover:text-slate-500 active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium text-slate-800">{stop.label}</span>
        {stop.city && (
          <span className="truncate text-[11px] font-normal text-slate-400">
            {stop.city}
          </span>
        )}
      </span>
      {tw && rc && (
        <span className={cn("rounded px-1 text-[11px] font-semibold", rc.bg, rc.text)}>
          {tw}
        </span>
      )}
      {badge && (
        <span className="shrink-0 rounded bg-emerald-600 px-1 text-[11px] font-semibold text-white">
          {badge}
        </span>
      )}
    </div>
  );
}

/**
 * Hook: a draggable list of dispatch ids (pointer-capture + elementFromPoint).
 */
function useDragList(initial: string[]) {
  const [order, setOrder] = useState<string[]>(initial);
  const listRef = useRef<HTMLDivElement | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const reorderTo = (from: string, to: string) =>
    setOrder((cur) => {
      if (from === to) return cur;
      const f = cur.indexOf(from);
      const t = cur.indexOf(to);
      if (f < 0 || t < 0) return cur;
      const next = cur.slice();
      next.splice(f, 1);
      next.splice(t, 0, from);
      return next;
    });
  const start = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    dragIdRef.current = id;
    setDragId(id);
    try {
      listRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };
  const move = (e: React.PointerEvent) => {
    const id = dragIdRef.current;
    if (!id) return;
    const over = document
      .elementFromPoint(e.clientX, e.clientY)
      ?.closest("[data-stopid]")
      ?.getAttribute("data-stopid");
    if (over && over !== id) reorderTo(id, over);
  };
  const end = (e: React.PointerEvent) => {
    dragIdRef.current = null;
    try {
      if (listRef.current?.hasPointerCapture(e.pointerId))
        listRef.current.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    setDragId(null);
  };
  return { order, setOrder, listRef, dragId, start, move, end };
}

/**
 * ReorderPreview — the same review-before-commit step for the "re-run this
 * route in a tighter order" recommendation. Seth: "I'd like a preview before it
 * just applies that." Shows the tech's CURRENT order next to the PROPOSED
 * (optimized) order — draggable, so you can tweak it — with miles before/after,
 * then Confirm / Cancel.
 */
export function ReorderPreview({
  pendingReorder,
  rows,
  onConfirm,
  onClose,
  pending = false,
}: {
  pendingReorder: PendingReorder;
  rows: Dispatch[];
  onConfirm: (order: string[]) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const byId = new Map(rows.map((r) => [r.dispatch, r] as const));
  const stopById = new Map(pendingReorder.stops.map((s) => [s.dispatch, s] as const));
  const pick = (ids: string[]) =>
    ids.map((id) => stopById.get(id)).filter((s): s is RecoStop => Boolean(s));

  const { order, listRef, dragId, start, move, end } = useDragList(
    pendingReorder.proposedOrder,
  );

  const currentStops = pick(pendingReorder.currentOrder);
  const proposedStops = pick(order);
  const currentMi = routeMiles(currentStops);
  const proposedMi = routeMiles(proposedStops);
  const saved = currentMi - proposedMi;

  // Real driving for both orders (Distance Matrix, cached) — proposed updates
  // live as the dispatcher drags.
  const currentDrive = useRouteDrive(currentStops).data;
  const proposedDrive = useRouteDrive(proposedStops).data;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          Re-order {pendingReorder.techName}&apos;s route
          <InfoTip label="How re-ordering works">
            Drag any stop in the proposed column to tweak the order before saving — the miles update as you go. Time
            windows are respected by the optimizer; dragging is your manual override.
          </InfoTip>
        </span>
      }
      subtitle={
        saved > 0.1 ? (
          <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">
            saves ~{saved.toFixed(1)} mi
          </span>
        ) : (
          "no improvement"
        )
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(order)}
            disabled={pending}
            className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            {pending ? "Saving…" : "Save this order"}
          </button>
        </div>
      }
    >
      <div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  Current order
                </span>
                <span className="text-right text-[11px] tabular-nums text-slate-500">
                  {currentMi.toFixed(0)} mi
                  {currentDrive?.complete && (
                    <span className="block font-medium text-emerald-700">
                      actual {currentDrive.totalMiles.toFixed(0)} mi ·{" "}
                      {Math.round(currentDrive.totalMinutes)} min
                    </span>
                  )}
                </span>
              </div>
              <div className="space-y-1">
                {currentStops.map((s) => (
                  <StopRow key={s.dispatch} stop={s} rows={byId} tone="stay" />
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  Proposed order
                </span>
                <span className="text-right text-[11px] tabular-nums text-slate-500">
                  {proposedMi.toFixed(0)} mi
                  {proposedDrive?.complete && (
                    <span className="block font-medium text-emerald-700">
                      actual {proposedDrive.totalMiles.toFixed(0)} mi ·{" "}
                      {Math.round(proposedDrive.totalMinutes)} min
                    </span>
                  )}
                </span>
              </div>
              <div
                ref={listRef}
                onPointerMove={move}
                onPointerUp={end}
                onPointerCancel={end}
                className="select-none space-y-1"
              >
                {proposedStops.map((s) => (
                  <StopRow
                    key={s.dispatch}
                    stop={s}
                    rows={byId}
                    tone="new"
                    onDragStart={(e) => start(e, s.dispatch)}
                    dragging={dragId === s.dispatch}
                  />
                ))}
              </div>
            </div>
          </div>
      </div>
    </Modal>
  );
}

export function MovePreview({
  move,
  rows,
  recoTechs,
  onConfirm,
  onClose,
  pending = false,
  previewOnly = false,
  initialOrder,
  onOrderChange,
}: {
  move: PendingMove;
  rows: Dispatch[];
  recoTechs: RecoTech[];
  /** edited = the dispatcher dragged the target order (so persist it). */
  onConfirm: (targetOrder: string[], edited: boolean) => void;
  onClose: () => void;
  pending?: boolean;
  /** Read-only: show the before/after only, no commit button (whole-day propose). */
  previewOnly?: boolean;
  /** Seed the target order from a remembered tweak (so re-opening keeps edits). */
  initialOrder?: string[];
  /** Fired whenever the target order changes — lets the caller remember the tweak. */
  onOrderChange?: (order: string[]) => void;
}) {
  const byId = new Map(rows.map((r) => [r.dispatch, r] as const));
  const moved = byId.get(move.dispatch) ?? null;
  const fromTech = recoTechs.find((t) => t.empno === move.fromEmpno);
  const toTech = recoTechs.find((t) => t.empno === move.toEmpno);
  const movedStop = fromTech?.stops.find((s) => s.dispatch === move.dispatch);

  // Window-aware optimized order (realistic, drivable) for the static routes.
  const fromStops = optimizeBanded(fromTech?.stops ?? []);
  const fromAfterStops = optimizeBanded(
    (fromTech?.stops ?? []).filter((s) => s.dispatch !== move.dispatch),
  );
  const toBeforeStops = optimizeBanded(toTech?.stops ?? []);

  // The target's "after" route is editable: seed it with the optimized order,
  // then let the dispatcher drag it. Look up stops by id so we can re-render any
  // order. (Component remounts per move, so the seed runs fresh each time.)
  // Multitech guard (AUDIT A2): on a 2-tech job the target may ALREADY carry
  // this dispatch — appending it again rendered the stop twice and counted it
  // twice in the mileage. Only append when the target doesn't have it.
  const targetAlreadyHas = (toTech?.stops ?? []).some(
    (s) => s.dispatch === move.dispatch,
  );
  const toPool: RecoStop[] = [
    ...(toTech?.stops ?? []),
    ...(movedStop && !targetAlreadyHas ? [movedStop] : []),
  ];
  const toStopById = new Map(toPool.map((s) => [s.dispatch, s] as const));
  const [toOrder, setToOrder] = useState<string[]>(() => {
    const optimized = optimizeBanded(toPool).map((s) => s.dispatch);
    // Re-opening a move keeps the dispatcher's earlier drag (the remembered tweak).
    if (initialOrder && initialOrder.length) {
      const poolIds = new Set(toPool.map((s) => s.dispatch));
      const seeded = initialOrder.filter((id) => poolIds.has(id));
      const missing = optimized.filter((id) => !seeded.includes(id));
      if (seeded.length) return [...seeded, ...missing];
    }
    return optimized;
  });
  // Tell the caller when the order changes (skip the first render so merely
  // OPENING a preview isn't counted as a tweak — only real drags are).
  const orderMounted = useRef(false);
  useEffect(() => {
    if (!orderMounted.current) {
      orderMounted.current = true;
      return;
    }
    onOrderChange?.(toOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toOrder]);
  const initialOrderRef = useRef<string[] | null>(null);
  if (initialOrderRef.current === null) initialOrderRef.current = toOrder;
  const edited =
    JSON.stringify(toOrder) !== JSON.stringify(initialOrderRef.current);

  const toAfterStops = toOrder
    .map((id) => toStopById.get(id))
    .filter((s): s is RecoStop => Boolean(s));

  const fromBeforeMi = routeMiles(fromStops);
  const fromAfterMi = routeMiles(fromAfterStops);
  const toBeforeMi = routeMiles(toBeforeStops);
  const toAfterMi = routeMiles(toAfterStops);
  const net = fromBeforeMi + toBeforeMi - (fromAfterMi + toAfterMi);

  // Real driving (Distance Matrix, cached per leg) for the before/after of both
  // techs — updates live as the dispatcher drags the new stop around.
  const fromBeforeDrive = useRouteDrive(fromStops).data;
  const fromAfterDrive = useRouteDrive(fromAfterStops).data;
  const toBeforeDrive = useRouteDrive(toBeforeStops).data;
  const toAfterDrive = useRouteDrive(toAfterStops).data;
  const driveLine = (
    before: { totalMinutes: number; complete: boolean } | null,
    after: { totalMinutes: number; complete: boolean } | null,
  ) =>
    before?.complete && after?.complete
      ? `actual ${Math.round(before.totalMinutes)} → ${Math.round(after.totalMinutes)} min driving`
      : null;
  const fromDriveLine = driveLine(fromBeforeDrive, fromAfterDrive);
  const toDriveLine = driveLine(toBeforeDrive, toAfterDrive);

  // Drag-to-reorder the target list (same pointer pattern as the route editor).
  const listRef = useRef<HTMLDivElement | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const reorderTo = (fromIdv: string, toIdv: string) =>
    setToOrder((cur) => {
      if (fromIdv === toIdv) return cur;
      const f = cur.indexOf(fromIdv);
      const t = cur.indexOf(toIdv);
      if (f < 0 || t < 0) return cur;
      const next = cur.slice();
      next.splice(f, 1);
      next.splice(t, 0, fromIdv);
      return next;
    });
  const startDrag = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    dragIdRef.current = id;
    setDragId(id);
    try {
      listRef.current?.setPointerCapture(e.pointerId);
    } catch {
      // capture unsupported — move/up still work
    }
  };
  const onDragMove = (e: React.PointerEvent) => {
    const id = dragIdRef.current;
    if (!id) return;
    const row = document
      .elementFromPoint(e.clientX, e.clientY)
      ?.closest("[data-stopid]");
    const overD = row?.getAttribute("data-stopid");
    if (overD && overD !== id) reorderTo(id, overD);
  };
  const endDrag = (e: React.PointerEvent) => {
    dragIdRef.current = null;
    try {
      if (listRef.current?.hasPointerCapture(e.pointerId)) {
        listRef.current.releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore
    }
    setDragId(null);
  };

  const { timeWindow, priority } = resolveWindowAndPriority(
    moved?.window_code,
    moved?.notes,
  );
  // Pegasus moved stop has no ESC row — read its time/window/city off the RecoStop.
  const headerWin = timeWindow ?? movedStop?.win ?? null;
  const wc = headerWin ? windowColorClasses(headerWin) : null;
  const pc = priority ? windowColorClasses(priority) : null;
  const headerCity = moved?.city ?? movedStop?.city ?? null;
  const typePreview = extractJobPreview(moved?.notes, 80);

  const rowMeta = (stop: RecoStop) => {
    const r = byId.get(stop.dispatch);
    const rw = resolveWindowAndPriority(r?.window_code, r?.notes);
    // Pegasus stops carry their own time/window (no ESC row to read).
    const tw = rw.timeWindow ?? stop.win ?? null;
    const rc = tw ? windowColorClasses(tw) : null;
    return { time: r ? formatTime(r.tpromtime) : stop.clock ?? "", tw, rc };
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          Review Move
          <InfoTip label="How the move works">
            Routes are shown in optimized order (shortest drive within each time window). Drag the grip to slide the call
            where you want it in the new tech&apos;s day — the miles update as you go.
          </InfoTip>
        </span>
      }
      subtitle={
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-slate-700">{move.fromName}</span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-medium text-slate-700">{move.toName}</span>
          {net > 0.1 ? (
            <span className="ml-1 rounded-full bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">
              saves ~{net.toFixed(1)} mi
            </span>
          ) : net < -0.1 ? (
            <span className="ml-1 rounded-full bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700">
              +{Math.abs(net).toFixed(1)} mi
            </span>
          ) : null}
        </span>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            {previewOnly ? "Close" : "Cancel"}
          </button>
          {!previewOnly && (
            <button
              type="button"
              onClick={() => onConfirm(toOrder, edited)}
              disabled={pending}
              className="rounded-md bg-brand-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-e1 transition-colors hover:bg-brand-800 disabled:bg-brand-300"
            >
              {pending ? "Moving…" : `Confirm move to ${move.toName}`}
            </button>
          )}
        </div>
      }
    >
      <div>
          {/* The call being moved */}
          <div className="mb-4 rounded-xl border border-hairline bg-slate-50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">
                {move.label}
              </span>
              {headerWin && wc && (
                <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-semibold", wc.bg, wc.text)}>
                  {headerWin}
                </span>
              )}
              {priority && pc && (
                <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase", pc.bg, pc.text)}>
                  {priority}
                </span>
              )}
              {headerCity && (
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                  <MapPin className="h-3 w-3" /> {headerCity}
                </span>
              )}
            </div>
            <p className="mt-1.5 text-xs leading-snug text-slate-600">
              {typePreview}
            </p>
          </div>

          {/* Side-by-side: source loses it (static), target gains it (draggable) */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  {move.fromName} — leaves
                </span>
                <span className="text-right text-[11px] tabular-nums text-slate-500">
                  {fromStops.length} → {fromAfterStops.length} stops ·{" "}
                  {fromBeforeMi.toFixed(0)} → {fromAfterMi.toFixed(0)} mi
                  {fromDriveLine && (
                    <span className="block font-medium text-emerald-700">
                      {fromDriveLine}
                    </span>
                  )}
                </span>
              </div>
              <div className="space-y-1">
                {fromStops.map((s) => {
                  const m = rowMeta(s);
                  const leaving = s.dispatch === move.dispatch;
                  return (
                    <div
                      key={s.dispatch}
                      className={cn(
                        "flex items-center gap-2 rounded-md border px-2 py-1 text-xs",
                        leaving
                          ? "border-rose-200 bg-rose-50 text-slate-400 line-through"
                          : "border-hairline bg-white",
                      )}
                    >
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium text-slate-800">
                          {s.label}
                        </span>
                        {s.city && (
                          <span className="truncate text-[11px] font-normal text-slate-400">
                            {s.city}
                          </span>
                        )}
                      </span>
                      {m.tw && m.rc && (
                        <span className={cn("rounded px-1 text-[11px] font-semibold", m.rc.bg, m.rc.text)}>
                          {m.tw}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="text-xs font-semibold text-slate-700">
                  {move.toName} — gains
                </span>
                <span className="text-right text-[11px] tabular-nums text-slate-500">
                  {toBeforeStops.length} → {toAfterStops.length} stops ·{" "}
                  {toBeforeMi.toFixed(0)} → {toAfterMi.toFixed(0)} mi
                  {toDriveLine && (
                    <span className="block font-medium text-emerald-700">
                      {toDriveLine}
                    </span>
                  )}
                </span>
              </div>
              <div
                ref={listRef}
                onPointerMove={onDragMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                className="select-none space-y-1"
              >
                {toAfterStops.map((s) => {
                  const m = rowMeta(s);
                  const isNew = s.dispatch === move.dispatch;
                  return (
                    <div
                      key={s.dispatch}
                      data-stopid={s.dispatch}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-shadow",
                        isNew
                          ? "border-emerald-300 bg-emerald-50"
                          : "border-hairline bg-white",
                        dragId === s.dispatch &&
                          "pointer-events-none scale-[1.02] opacity-95 shadow-e2",
                      )}
                    >
                      <span
                        onPointerDown={(e) => startDrag(e, s.dispatch)}
                        aria-label="Drag to reorder"
                        className="shrink-0 cursor-grab touch-none text-slate-300 transition-colors hover:text-slate-500 active:cursor-grabbing"
                      >
                        <GripVertical className="h-3.5 w-3.5" />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium text-slate-800">
                          {s.label}
                        </span>
                        {s.city && (
                          <span className="truncate text-[11px] font-normal text-slate-400">
                            {s.city}
                          </span>
                        )}
                      </span>
                      {m.tw && m.rc && (
                        <span className={cn("rounded px-1 text-[11px] font-semibold", m.rc.bg, m.rc.text)}>
                          {m.tw}
                        </span>
                      )}
                      {isNew && (
                        <span className="shrink-0 rounded bg-emerald-600 px-1 text-[11px] font-semibold text-white">
                          NEW
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {edited && (
            <p className="mt-3 text-[12px] font-medium text-brand-600">Your order will be saved.</p>
          )}
      </div>
    </Modal>
  );
}
